import { apiClient } from "@/api";
import { Comment, Task, Vote, VoteValue, User, Role } from "@/api/types";
import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faHistory,
  faLink,
  faLock, faMagicWandSparkles,
  faMinusCircle,
  faPencil,
  faPlusCircle,
  faStar,
  faTags,
  faThumbsDown,
  faThumbsUp,
  faUserCircle, faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import RichTextEditor, {
  RichTextEditorRef,
} from "@/components/widgets/richtext/RichTextEditor";
import RichTextDisplay from "@/components/widgets/richtext/RichTextDisplay";
import AppButton, { ButtonVariants } from "@/components/structure/AppButton";
import { useAuth } from "@/contexts/AuthContext";
import toast from "@/utils/toast";
import { NavLink } from "react-router";
import { useProject } from "@/contexts/ProjectContext";
import { AnimatePresence, motion } from "motion/react";
import CommentHistory from "@/components/tasks/discuss/CommentHistory";
import { TimeAgo } from "@/components/widgets/TimeAgo";

enum ReplyType {
  Public = "Public",
  Private = "Private",
  Clarify = "Clarify",
}

type TagSelections = {
  contextuallyRelevant: boolean;
  contextuallyIrrelevant: boolean;
  requiresClarification: boolean;
  doesntRequireClarification: boolean;
};

const TAG_VALUES = {
  contextuallyRelevant: "Contextually Relevant",
  contextuallyIrrelevant: "Contextually Irrelevant",
  requiresClarification: "Requires Clarification",
  doesntRequireClarification: "Doesn't Require Clarification",
} as const;

const buildTagSelectionsFromTags = (tags: string[] | undefined): TagSelections => {
  const currentTags = tags ?? [];
  return Object.entries(TAG_VALUES).reduce((selections, [key, value]) => {
    return {
      ...selections,
      [key]: currentTags.includes(value),
    };
  }, {} as TagSelections);
};

const buildTagsFromSelections = (selections: TagSelections): string[] => {
  const tags: string[] = [];
  Object.entries(selections).forEach(([key, value]) => {
    if (value) {
      const tagValue = TAG_VALUES[key as keyof typeof TAG_VALUES];
      if (tagValue) {
        tags.push(tagValue);
      }
    }
  });
  return tags;
};

const areTagListsEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  return left.every((tag, idx) => tag === right[idx]);
};

function hashString(str) {
  let hash = 2166136261;

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash +=
      (hash << 1) +
      (hash << 4) +
      (hash << 7) +
      (hash << 8) +
      (hash << 24);
  }

  return hash >>> 0;
}

function stringToColor(str) {
  const hash = hashString(str);

  const hue = hash % 360;

  // Pastel ranges
  const saturation = 40 + (hash % 20); // 40–60%
  const lightness = 82 + ((hash >> 8) % 10); // 82–92%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export interface CommentItemProps {
  comment: Comment;
  task: Task;
  onAddReply: (commentId: string, parentReplyId?: string) => void;
  onUpdateTags?: () => void;
  depth?: number;
  user: User | null;
}

export function CommentItem({
  comment,
  task,
  onAddReply,
  onUpdateTags,
  depth = 0,
  user,
}: CommentItemProps) {
  const { project } = useProject();
  const { isAuthenticated, user: currentUser } = useAuth();

  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isPublicReply, setIsPublicReply] = useState(comment.isPublic);
  const [isClarifyRequest, setIsClarifyRequest] = useState(false);
  const [repliesCollapsed, setRepliesCollapsed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSelections, setTagSelections] = useState<TagSelections>(
    () => buildTagSelectionsFromTags(comment.tags)
  );

  const richTextEditorRef = useRef<RichTextEditorRef>(null);
  const tagDropdownRef = useRef<HTMLDivElement | null>(null);
  const currentTagListRef = useRef<string[]>(comment.tags ?? []);

  const isReply = depth > 0;

  // Focus the editor when reply form is shown
  useEffect(() => {
    if (showReplyForm) {
      // Use a small delay to ensure the editor is fully mounted
      const timer = setTimeout(() => {
        richTextEditorRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showReplyForm]);

  useEffect(() => {
    if (!showTagDropdown) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showTagDropdown]);

  useEffect(() => {
    const nextTags = buildTagsFromSelections(tagSelections);
    const previousTags = currentTagListRef.current;

    // skip update if tags haven't changed
    if (areTagListsEqual(nextTags, previousTags)) {
      return;
    }

    currentTagListRef.current = nextTags;

    const syncTags = async () => {
      try {
        const updatedComment = await apiClient.patch<Comment>(`/comments/${comment.id}/`, {
          tags: nextTags,
        });
        const serverTags = updatedComment.tags ?? nextTags;
        currentTagListRef.current = serverTags;
        onUpdateTags(updatedComment.tags);
      } catch (error) {
        currentTagListRef.current = previousTags;
        setTagSelections(buildTagSelectionsFromTags(previousTags));
        console.error("Failed to save comment tags:", error);
        toast.error("Failed to update tags. Please try again.");
      }
    };

    syncTags();
  }, [tagSelections, comment.id]);

  const resetReplyBox = () => {
      setShowReplyForm(false);
      setIsEditing(false);
      setReplyContent("");
  }

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !user) return;

    setIsSubmitting(true);
    try {
      if (!isEditing) {
        await apiClient.post<Comment>(`/comments/`, {
          prompt: comment.prompt,
          content: replyContent.trim(),
          author: user.id,
          isPublic: isPublicReply,
          isClarifyRequest: isClarifyRequest,
          parentReply: comment.id,
        });
      } else {
        await apiClient.patch<Comment>(`/comments/${comment.id}/`, {
          content: replyContent.trim(),
          isPublic: isPublicReply,
          isClarifyRequest: isClarifyRequest,
        });
        setIsEditing(false);
      }

      resetReplyBox();
      onAddReply(comment.id);
    } catch (error) {
      console.error("Failed to submit reply:", error);
      toast.error("Failed to submit reply. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.delete(`/comments/${commentId}/`);
      toast.success("Comment deleted.");
      resetReplyBox();
      onAddReply(comment.id);
    } catch (error) {
      console.error("Failed to delete comment:", error);
      toast.error("Failed to delete comment. Either you're not allowed to do so or there are replies to this comment.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const voteOnComment = async (commentId: string, value: VoteValue) => {
    try {
      await apiClient.post<Vote>(`/comments/${commentId}/vote/`, {
        comment: commentId,
        voteValue: value === comment.currentUserVote ? 0 : value, // toggle vote if same button clicked
      });
      // update the UI?
      onAddReply(comment.id);
    } catch (error) {
      console.error("Failed to vote on comment:", error);
      toast.error("Failed to register your vote. Please try again.");
    }
  };

  const toggleReplies = () => {
    setRepliesCollapsed(!repliesCollapsed);
  };

  const replyTypeForComment = (comment: Comment | null): ReplyType => {
    if (!comment) return ReplyType.Public;

    if (comment?.isClarifyRequest) {
      return ReplyType.Clarify;
    }
    return comment?.isPublic ? ReplyType.Public : ReplyType.Private;
  };

  const currentReplyType = () => {
    if (isClarifyRequest) {
      return ReplyType.Clarify;
    }
    return isPublicReply ? ReplyType.Public : ReplyType.Private;
  };

  const replyVariants = {
    [ReplyType.Public]: "primary",
    [ReplyType.Private]: "warning",
    [ReplyType.Clarify]: "warning",
  };

  const replyBackgrounds = {
    [ReplyType.Public]: "",
    [ReplyType.Private]: "bg-orange-50",
    [ReplyType.Clarify]: "bg-yellow-50",
  };

  const replyText = (replyType: ReplyType) => {
    const editPrefix = isEditing ? "Edit " : "";

    if (isSubmitting) {
      return !isEditing ? "Posting..." : "Editing...";
    }

    if (replyType === ReplyType.Clarify) {
      return editPrefix + "Request Clarification";
    }
    return (
      editPrefix + (replyType === ReplyType.Public ? "Reply" : "Private Reply")
    );
  };

  const setReplyFormMode = (replyType: ReplyType, editing: boolean = false) => {
    if (replyType === ReplyType.Clarify) {
      setIsClarifyRequest(true);
      setIsPublicReply(false);
    } else if (replyType === ReplyType.Private) {
      setIsClarifyRequest(false);
      setIsPublicReply(false);
    } else {
      setIsClarifyRequest(false);
      setIsPublicReply(true);
    }

    setIsEditing(editing);
    setShowReplyForm(true);
  }

  const beginClarifyRequest = () => {
    // kicks off a clarify request, which starts with opening the reply box in clarify mode
    // then, the contents of the original comment are passed to an LLM that constructs a clarification request
    // the admin can then edit the resulting clarification before submitting
    setReplyFormMode(ReplyType.Clarify, false);

    // TODO: call API to generate clarification request text via LLM and set as replyContent
  }

  return (
    <div id={`comment-${comment.id}`} className={!isReply ? "mb-6" : ""}>
      <div className={isReply ? "relative ml-3 mt-2" : ""}>
        {/* Author Header, Badges */}
        <div className="flex justify-start items-start">
          {/* For replies, a little arc that connects to the parent comment/reply */}
          {isReply && (
            <div
              className="w-4 h-4 border-l border-b border-gray-300 rounded-bl-lg"
              style={{ position: "absolute", left: "-21px" }}
            ></div>
          )}

          <CommenterHeader comment={comment} setShowHistory={setShowHistory} />
        </div>

        {/* Comment Text */}
        {!repliesCollapsed && (
          <div
            className={`_reply-content transition-colors duration-300 ease-out text-gray-800 whitespace-pre-wrap border-l border-gray-300 pl-3 ml-4 ${
              depth > 0 ? "" : "bg-blue-50 py-2"
            } ${replyBackgrounds[replyTypeForComment(comment)]} py-1 px-1`}
          >
            <RichTextDisplay content={comment.content || "<i>&lt;empty response&gt;</i>"} />
          </div>
        )}

        {/* Button Tray */}
        <div className="flex items-center gap-2 ml-2">
          <FontAwesomeIcon
            icon={repliesCollapsed ? faPlusCircle : faMinusCircle}
            onClick={() => toggleReplies()}
            className="text-gray-400 text-lg cursor-pointer"
          />

          {!repliesCollapsed && (
            <div className="flex items-center justify-start">
              {/* Upvote, Score, Downvote */}
              <button
                type="button"
                onClick={() => voteOnComment(comment.id, 1)}
                className={`text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-yellow-300 p-2 px-3 rounded-full cursor-pointer ${
                  comment.currentUserVote === 1 ? "bg-yellow-200 !text-blue-500" : ""
                }`}
              >
                <FontAwesomeIcon icon={faThumbsUp} />
              </button>

              <span className="text-sm text-gray-600 px-2 min-w-10 text-center">
                {comment.totalVotes || 0}
              </span>

              <button
                type="button"
                onClick={() => voteOnComment(comment.id, -1)}
                className={`text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full cursor-pointer ${
                  comment.currentUserVote === -1 ? "bg-yellow-200 !text-red-500" : ""
                }`}
              >
                <FontAwesomeIcon icon={faThumbsDown} />
              </button>

              {/* Other actions to take on comment (reply, clarify, etc.) */}
              <button
                type="button"
                onClick={() => setReplyFormMode(ReplyType.Public, false)}
                disabled={isEditing}
                className={`text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full ${isEditing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <FontAwesomeIcon icon={faComment} className="mr-1" />
                Reply
              </button>

              <button
                type="button"
                onClick={() => setReplyFormMode(ReplyType.Private, false)}
                disabled={isEditing}
                className={`text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full ${isEditing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <FontAwesomeIcon icon={faLock} className="mr-1" />
                Private Reply
              </button>

              {task?.roles?.includes(Role.Facilitator) && (
                <button
                  type="button"
                  disabled={isEditing}
                  onClick={() => setReplyFormMode(ReplyType.Clarify, false)}
                  className={`text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full ${isEditing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <FontAwesomeIcon
                    icon={faWandMagicSparkles}
                    className="mr-1"
                  />
                  Clarify
                </button>
              )}

              {task?.roles?.includes(Role.Facilitator) && (
                <div ref={tagDropdownRef} className="relative">
                  <button
                    type="button"
                    disabled={isEditing}
                    onClick={() => setShowTagDropdown((prev) => !prev)}
                    className={`text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full ${isEditing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <FontAwesomeIcon
                      icon={faTags}
                      className="mr-1"
                    />
                    Tag
                  </button>

                  {showTagDropdown && (
                    <div
                      className="absolute z-20 mt-2 w-72 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Tags
                      </p>

                      <div className="space-y-2 text-sm">
                        <label
                          className={`flex items-center gap-2 ${
                            tagSelections.contextuallyIrrelevant ? "opacity-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={tagSelections.contextuallyRelevant}
                            className={`${tagSelections.contextuallyIrrelevant ? "opacity-50" : ""}`}
                            onChange={(e) =>
                              setTagSelections((prev) => ({
                                ...prev,
                                contextuallyRelevant: e.target.checked,
                                contextuallyIrrelevant: false
                              }))
                            }
                          />
                          Contextually Relevant
                        </label>

                        <label
                          className={`flex items-center gap-2 ${
                            tagSelections.contextuallyRelevant ? "opacity-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={tagSelections.contextuallyIrrelevant}
                            className={`${tagSelections.contextuallyRelevant ? "opacity-50" : ""}`}
                            onChange={(e) =>
                              setTagSelections((prev) => ({
                                ...prev,
                                contextuallyIrrelevant: e.target.checked,
                                contextuallyRelevant: false
                              }))
                            }
                          />
                          Contextually Irrelevant
                        </label>

                        <label
                          className={`flex items-center gap-2 ${
                            tagSelections.doesntRequireClarification ? "opacity-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={tagSelections.requiresClarification}
                            className={`${tagSelections.doesntRequireClarification ? "opacity-50" : ""}`}
                            onChange={(e) =>
                              setTagSelections((prev) => ({
                                ...prev,
                                requiresClarification: e.target.checked,
                                doesntRequireClarification: false
                              }))
                            }
                          />
                          Requires Clarification
                        </label>

                        <label
                          className={`flex items-center gap-2 ${
                            tagSelections.requiresClarification ? "opacity-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={tagSelections.doesntRequireClarification}
                            className={`${tagSelections.requiresClarification ? "opacity-50" : ""}`}
                            onChange={(e) =>
                              setTagSelections((prev) => ({
                                ...prev,
                                doesntRequireClarification: e.target.checked,
                                requiresClarification: false
                              }))
                            }
                          />
                          Doesn't Require Clarification
                        </label>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* Link to this comment button */}
              <button
                type="button"
                onClick={() => {
                  // change the URL without reloading the page
                  window.history.replaceState(
                    null,
                    "",
                    `#comment-${comment.id}`
                  );

                  // navigator.clipboard.writeText(
                  //   `${window.location.origin}${window.location.pathname}#comment-${comment.id}`
                  // );
                  // toast.success("Link to comment copied to clipboard!");
                }}
                className="text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full"
              >
                <FontAwesomeIcon icon={faLink} className="mr-1" />
                Link
              </button>

              {/* Edit button (only for own comments) */}
              {isAuthenticated && currentUser?.id === comment.author && (
                <button
                  type="button"
                  onClick={() => { setReplyContent(comment.content); setReplyFormMode(replyTypeForComment(comment), true) }}
                  className="text-gray-400 hover:text-gray-800 text-sm font-medium hover:bg-gray-300 p-2 px-3 rounded-full"
                >
                  <FontAwesomeIcon icon={faPencil} className="mr-1" />
                  Edit
                </button>
              )}
            </div>
          )}

          {repliesCollapsed && (
            <span className="text-sm text-gray-500">
              {comment.replies?.length}{" "}
              {comment.replies?.length === 1 ? "reply" : "replies"}
            </span>
          )}
        </div>

        {/* Reply Form */}
        <AnimatePresence initial={false} mode="popLayout">
          {showReplyForm && user && (
            <motion.div
              layout
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
            >
              <form
                onSubmit={handleSubmitReply}
                className={`mt-3 pt-3 ml-4 border-l border-gray-300 pl-6 ${
                  isReply ? "pb-3" : ""
                }`}
              >
                {/* Reply Content */}
                <RichTextEditor
                  ref={richTextEditorRef}
                  initialValue={replyContent}
                  onChange={(value) => setReplyContent(value)}
                  placeholder="Write your reply..."
                  embedImagesInBody={true}
                  rows={1}
                  enabledToolbarBlocks={[
                    "formatting",
                    "lists",
                    "tables",
                    "history",
                  ]}
                  disabled={isSubmitting}
                />

                {/* Cancel, Post Reply Buttons */}
                <div className="flex items-center justify-end mt-3 gap-2">
                  <AppButton
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      resetReplyBox();
                    }}
                    disabled={isSubmitting}
                    className="min-w-30 justify-center"
                  >
                    Cancel
                  </AppButton>

                  {isEditing && (
                    <AppButton
                      type="button"
                      variant="danger"
                      onClick={() => deleteComment(comment.id)}
                      disabled={isSubmitting}
                      className="min-w-30 justify-center"
                    >
                      Delete
                    </AppButton>
                  )}

                  <AppButton
                    type="submit"
                    variant={
                      replyVariants[
                        currentReplyType()
                      ] as keyof typeof ButtonVariants
                    }
                    disabled={!replyContent.trim() || isSubmitting}
                    className="min-w-30 justify-center"
                  >
                    {replyText(currentReplyType())}
                  </AppButton>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Comment history modal */}
      {showHistory && (
        <CommentHistory
          commentId={comment.id}
          onClose={() => setShowHistory(false)}
        />
      )}

      {/* Render replies */}
      <AnimatePresence initial={false} mode="popLayout">
      {!repliesCollapsed && comment.replies && comment.replies.length > 0 && (
        <motion.div
          layout
          key={`replies-container-${comment.id}`}
          className={`${
            isReply ? "mt-2 ml-7" : "mt-3 ml-4"
          } border-l border-gray-300 pl-2`}
          initial={{ opacity: 0, y: -10, x: -30 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 10, x: 0 }}
        >
          
            {comment.replies.map((reply: Comment) => (
              <motion.div
                layout
                key={`reply-frame-${reply.id}`}
                initial={{ opacity: 0, y: -10, x: 0 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                exit={{ opacity: 0, y: 10, x: 0 }}
              >
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  task={task}
                  onAddReply={onAddReply}
                  user={user}
                  depth={depth + 1}
                />
              </motion.div>
            ))}          
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

/*
==========================================================================================================
=== Shared components used in CommentItem and elsewhere
==========================================================================================================
*/

export function CommenterHeader({
  comment,
  setShowHistory,
}: {
  comment: Comment;
  setShowHistory?: (show: boolean) => void;
}) {
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="flex justify-between items-start mb-2">
      <div className="text-sm text-gray-600 items-center flex space-x-1 ml-0.5">
        {comment.authorAvatarUrl ? (
          <img
            src={comment.authorAvatarUrl}
            alt={comment.authorName || `User ${comment.author}`}
            className="w-8 h-8 rounded-full mr-1"
          />
        ) : (
          <FontAwesomeIcon
            icon={faUserCircle}
            className="mr-1 text-gray-400 text-4xl"
          />
        )}

        <div className="flex space-x-1 items-center">
          <NavLink
            to={`/profile/${comment.author}`}
            className="font-bold hover:underline"
          >
            {comment.authorName || `User ${comment.author}`}
          </NavLink>
          <span>•</span>
          <TimeAgo timestamp={comment.createdAt} />
          {comment.isPublic ? (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
              Public
            </span>
          ) : (
            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
              <FontAwesomeIcon icon={faLock} className="mr-1" />
              Facilitators Only
            </span>
          )}
          {comment.isClarifyRequest && (
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
              <FontAwesomeIcon icon={faMagicWandSparkles} className="mr-1" />
              Clarification Request
            </span>
          )}
          {isAuthenticated && user?.id === comment.author && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              <FontAwesomeIcon icon={faStar} /> You
            </span>
          )}

          {/* Badges for tags, where each tag gets a unique color based on its name */}
          {comment.tags?.map((tag) => (
            <span
              key={tag}
              className="text-xs text-black px-2 py-1 rounded"
              style={tag ? { backgroundColor: stringToColor(tag) } : {}}
            >
              <FontAwesomeIcon icon={faTags} className="mr-1" />
              {tag}
            </span>
          ))}

          {comment.updatedAt && setShowHistory && (
            <>
              <span>•</span>
              <button
                onClick={() => {
                  // open the comment history modal
                  setShowHistory(true);
                }}
                className="text-gray-600 hover:text-gray-800 underline text-sm"
              >
                <FontAwesomeIcon icon={faHistory} className="mr-1" />
                edited
              </button>
              <TimeAgo timestamp={comment.updatedAt} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
